import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { FaSave, FaPlus, FaTrash } from "react-icons/fa";

const AdminFooter = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("contact");
    const [footerConfig, setFooterConfig] = useState({
        description: "",
        address: "",
        mapsLink: "",
        phone: "",
        workingHours: "",
        zalo: "",
        email: "",
        facebook: "",
        instagram: "",
        tiktok: "",
        youtube: "",
        businessname: "",
        taxid: "",
        issuedby: "",
        issueddate: "",
        showbusinessinfo: true,
        categories: []
    });

    useEffect(() => {
        fetchFooterConfig();
    }, []);

    const fetchFooterConfig = async () => {
        try {
            setLoading(true);
            const response = await api.get("/settings/footer_config");
            console.log("DEBUG: AdminFooter response raw:", response.data);
            
            const extractValue = (data) => {
                if (data === null || data === undefined) return null;
                
                // If it's a simple value, return it
                if (typeof data !== 'object') return data;

                // Handle Array of Key-Value pairs (Recursive)
                if (Array.isArray(data)) {
                    const isKV = data.length > 0 && data.every(item => 
                        item && typeof item === 'object' && 
                        (('Key' in item && 'Value' in item) || ('key' in item && 'value' in item))
                    );

                    if (isKV) {
                        return data.reduce((acc, item) => {
                            const k = (item.Key || item.key)?.toLowerCase();
                            const v = item.Value !== undefined ? item.Value : item.value;
                            if (k) acc[k] = extractValue(v); // Recursive for value
                            return acc;
                        }, {});
                    }

                    // Not a KV array, just recurse on elements
                    return data.map(extractValue);
                }

                // Handle Objects with .value or .Value wrapping
                if (data.value !== undefined) return extractValue(data.value);
                if (data.Value !== undefined) return extractValue(data.Value);
                
                // Regular object: recurse on properties and normalize keys to lowercase
                return Object.keys(data).reduce((acc, key) => {
                    acc[key.toLowerCase()] = extractValue(data[key]);
                    return acc;
                }, {});
            };

            const config = extractValue(response.data);
            if (config) {
                console.log("DEBUG: AdminFooter extracted:", config);
                
                // Normalization helper for categories
                const normalizeCategories = (cats) => {
                    if (!Array.isArray(cats)) return [];
                    return cats.map(c => ({
                        name: c.name || c.Name || "",
                        href: c.href || c.Href || ""
                    }));
                };

                // Handle both "categories" and "Categories" (due to BSON/JSON casing issues)
                let finalCats = config.categories || config.Categories || [];
                
                // Ensure categories is an array
                if (typeof finalCats === 'string') {
                    try { finalCats = JSON.parse(finalCats); } catch(e) { finalCats = []; }
                }
                
                setFooterConfig(prev => ({ 
                    ...prev, 
                    ...config,
                    categories: normalizeCategories(finalCats)
                }));
            }
        } catch (error) {
            console.error("DEBUG: AdminFooter fetch error:", error);
            toast.error("Không thể tải cấu hình Footer");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.put("/admin/settings/footer_config", footerConfig);
            // Trigger refresh in other components (lik Footer)
            window.dispatchEvent(new Event('footer_config_updated'));
            toast.success("Cập nhật Footer thành công!");
        } catch (error) {
            toast.error("Lỗi khi cập nhật cấu hình");
        }
    };

    const addCategory = () => {
        const currentCats = Array.isArray(footerConfig.categories) ? footerConfig.categories : [];
        setFooterConfig({
            ...footerConfig,
            categories: [...currentCats, { name: "", href: "" }]
        });
    };

    const removeCategory = (index) => {
        const currentCats = Array.isArray(footerConfig.categories) ? footerConfig.categories : [];
        const newCats = currentCats.filter((_, i) => i !== index);
        setFooterConfig({ ...footerConfig, categories: newCats });
    };

    const updateCategory = (index, field, value) => {
        const currentCats = Array.isArray(footerConfig.categories) ? footerConfig.categories : [];
        const newCats = [...currentCats];
        newCats[index] = { ...newCats[index], [field]: value };
        setFooterConfig({ ...footerConfig, categories: newCats });
    };

    if (loading) return <div className="p-8 text-center text-gray-500 font-medium">Đang tải cấu hình...</div>;

    return (
        <div className="p-3 sm:p-8 max-w-5xl mx-auto transition-all duration-300">
            <div className="mb-6 sm:mb-8">
                <h1 className="text-xl sm:text-2xl font-black text-gray-900 uppercase tracking-tight">Quản lý Footer</h1>
                <p className="text-[10px] sm:text-base text-gray-500 font-medium mt-1">Cấu hình thông tin liên hệ và các liên kết chân trang</p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-8 bg-gray-100 p-1.5 rounded-2xl w-full sm:w-fit">
                <button
                    onClick={() => setActiveTab("contact")}
                    className={`px-3 sm:px-6 py-3 sm:py-2.5 rounded-xl font-bold text-xs sm:text-base transition-all ${activeTab === "contact" ? "bg-white text-red-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                    Thông tin liên hệ
                </button>
                <button
                    onClick={() => setActiveTab("categories")}
                    className={`px-3 sm:px-6 py-3 sm:py-2.5 rounded-xl font-bold text-xs sm:text-base transition-all ${activeTab === "categories" ? "bg-white text-red-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                    Danh mục nổi bật
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                {activeTab === "contact" ? (
                    <div className="bg-white rounded-[1.5rem] sm:rounded-[2.5rem] shadow-sm border border-gray-100 p-5 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <div className="md:col-span-2 space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Địa chỉ cửa hàng</label>
                            <input
                                type="text"
                                value={footerConfig.address}
                                onChange={(e) => setFooterConfig({ ...footerConfig, address: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-medium text-xs sm:text-base"
                            />
                        </div>
                        <div className="md:col-span-2 space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Link bản đồ (Google Maps URL)</label>
                            <input
                                type="text"
                                placeholder="Ví dụ: https://maps.app.goo.gl/..."
                                value={footerConfig.mapsLink}
                                onChange={(e) => setFooterConfig({ ...footerConfig, mapsLink: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-medium text-xs sm:text-base"
                            />
                        </div>
                        <div className="md:col-span-2 space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Mô tả cửa hàng (Footer)</label>
                            <textarea
                                rows="3"
                                value={footerConfig.description}
                                onChange={(e) => setFooterConfig({ ...footerConfig, description: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-medium text-xs sm:text-base"
                                placeholder="Mô tả ngắn về cửa hàng..."
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Số điện thoại</label>
                            <input
                                type="text"
                                value={footerConfig.phone}
                                onChange={(e) => setFooterConfig({ ...footerConfig, phone: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-bold text-xs sm:text-base"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Giờ làm việc</label>
                            <input
                                type="text"
                                value={footerConfig.workingHours}
                                onChange={(e) => setFooterConfig({ ...footerConfig, workingHours: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-bold text-xs sm:text-base"
                                placeholder="Ví dụ: 08:30 - 22:00"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Zalo</label>
                            <input
                                type="text"
                                value={footerConfig.zalo}
                                onChange={(e) => setFooterConfig({ ...footerConfig, zalo: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-bold text-xs sm:text-base"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Email</label>
                            <input
                                type="email"
                                value={footerConfig.email}
                                onChange={(e) => setFooterConfig({ ...footerConfig, email: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all text-xs sm:text-base"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Facebook URL</label>
                            <input
                                type="text"
                                value={footerConfig.facebook}
                                onChange={(e) => setFooterConfig({ ...footerConfig, facebook: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all text-xs sm:text-base"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Instagram URL</label>
                            <input
                                type="text"
                                value={footerConfig.instagram}
                                onChange={(e) => setFooterConfig({ ...footerConfig, instagram: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all text-xs sm:text-base"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">TikTok URL</label>
                            <input
                                type="text"
                                value={footerConfig.tiktok}
                                onChange={(e) => setFooterConfig({ ...footerConfig, tiktok: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all text-xs sm:text-base"
                            />
                        </div>

                        {/* Thông tin doanh nghiệp */}
                        <div className="md:col-span-2 mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                             <h3 className="text-gray-900 font-bold text-sm uppercase">Thông tin pháp lý / Doanh nghiệp (Chân trang)</h3>
                             <div className="flex items-center gap-3">
                                 <span className="text-[10px] font-bold text-gray-400 uppercase">{footerConfig.showbusinessinfo ? "Đang hiển thị" : "Đang ẩn"}</span>
                                 <button
                                     type="button"
                                     onClick={() => setFooterConfig({ ...footerConfig, showbusinessinfo: !footerConfig.showbusinessinfo })}
                                     className={`w-12 h-6 rounded-full transition-all relative ${footerConfig.showbusinessinfo ? "bg-red-500" : "bg-gray-200"}`}
                                 >
                                     <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${footerConfig.showbusinessinfo ? "right-1" : "left-1"}`}></div>
                                 </button>
                             </div>
                        </div>
                        <div className="md:col-span-2 space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Tên doanh nghiệp</label>
                            <input
                                type="text"
                                value={footerConfig.businessname}
                                onChange={(e) => setFooterConfig({ ...footerConfig, businessname: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-bold text-xs sm:text-base"
                                placeholder="Ví dụ: CÔNG TY TNHH TAHA SHOES"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Mã số thuế</label>
                            <input
                                type="text"
                                value={footerConfig.taxid}
                                onChange={(e) => setFooterConfig({ ...footerConfig, taxid: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-bold text-xs sm:text-base"
                                placeholder="Nhập mã số thuế"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Nơi cấp</label>
                            <input
                                type="text"
                                value={footerConfig.issuedby}
                                onChange={(e) => setFooterConfig({ ...footerConfig, issuedby: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-medium text-xs sm:text-base"
                                placeholder="Ví dụ: Sở Kế hoạch và Đầu tư TP.HCM"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block font-bold text-gray-700 uppercase tracking-wider text-[8px] sm:text-[10px]">Ngày cấp</label>
                            <input
                                type="text"
                                value={footerConfig.issueddate}
                                onChange={(e) => setFooterConfig({ ...footerConfig, issueddate: e.target.value })}
                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-medium text-xs sm:text-base"
                                placeholder="Ngày cấp (VD: 01/01/2024)"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-[1.5rem] sm:rounded-[2.5rem] shadow-sm border border-gray-100 p-4 sm:p-8 space-y-4">
                        <div className="flex justify-between items-center mb-4 sm:mb-6">
                            <h3 className="font-bold text-gray-700 uppercase tracking-widest text-[10px] sm:text-sm">Danh sách liên kết</h3>
                            <button
                                type="button"
                                onClick={addCategory}
                                className="flex items-center gap-1.5 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all font-bold text-[10px] sm:text-sm"
                            >
                                <FaPlus size={8} className="sm:text-[12px]" />
                                <span>Thêm</span>
                            </button>
                        </div>
                        
                        <div className="space-y-3 sm:space-y-3">
                            {(footerConfig.categories || []).map((cat, idx) => (
                                <div key={idx} className="flex gap-2 sm:gap-3 items-start sm:items-center bg-gray-50 p-2.5 sm:p-4 rounded-2xl border border-gray-100 group">
                                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                        <div className="space-y-1">
                                            <label className="sm:hidden text-[8px] font-bold text-gray-400 uppercase">Tên hiển thị</label>
                                            <input
                                                type="text"
                                                placeholder="Ví dụ: Nike"
                                                value={cat.name}
                                                onChange={(e) => updateCategory(idx, "name", e.target.value)}
                                                className="w-full bg-white border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-red-500 font-bold"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="sm:hidden text-[8px] font-bold text-gray-400 uppercase">Đường dẫn</label>
                                            <input
                                                type="text"
                                                placeholder="Ví dụ: /products?brand=Nike"
                                                value={cat.href}
                                                onChange={(e) => updateCategory(idx, "href", e.target.value)}
                                                className="w-full bg-white border border-gray-100 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeCategory(idx)}
                                        className="p-2 sm:p-3 text-gray-300 hover:text-red-500 transition-colors bg-white rounded-xl border border-gray-100 shadow-sm mt-4 sm:mt-0"
                                    >
                                        <FaTrash size={12} className="sm:text-[14px]" />
                                    </button>
                                </div>
                            ))}
                            {(!footerConfig.categories || footerConfig.categories.length === 0) && (
                                <div className="text-center py-10 text-gray-400 italic">Chưa có danh mục nào được thiết lập</div>
                            )}
                        </div>
                    </div>
                )}

                <button
                    type="submit"
                    className="w-full bg-red-600 text-white rounded-2xl py-3 sm:py-4 font-black uppercase tracking-widest hover:bg-red-700 transition-all shadow-xl shadow-red-500/20 flex items-center justify-center gap-3 hover:scale-[1.01] active:scale-[0.98] text-[10px] sm:text-base"
                >
                    <FaSave size={16} className="sm:text-[20px]" />
                    <span>Lưu cấu hình Footer</span>
                </button>
            </form>
        </div>
    );
};

export default AdminFooter;
